import Axios from 'axios';
import axiosRetry from 'axios-retry'; // Axios.prototype cannot be modified

var axiosExtra = {
  setBaseURL(baseURL) {
    this.defaults.baseURL = baseURL;
  },

  setHeader(name, value) {
    var scopes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'common';

    for (var scope of Array.isArray(scopes) ? scopes : [scopes]) {
      if (!value) {
        delete this.defaults.headers[scope][name];
        return;
      }

      this.defaults.headers[scope][name] = value;
    }
  },

  setToken(token, type) {
    var scopes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'common';
    var value = !token ? null : (type ? type + ' ' : '') + token;
    this.setHeader('Authorization', value, scopes);
  },

  onRequest(fn) {
    this.interceptors.request.use(config => fn(config) || config);
  },

  onResponse(fn) {
    this.interceptors.response.use(response => fn(response) || response);
  },

  onRequestError(fn) {
    this.interceptors.request.use(undefined, error => fn(error) || Promise.reject(error));
  },

  onResponseError(fn) {
    this.interceptors.response.use(undefined, error => fn(error) || Promise.reject(error));
  },

  onError(fn) {
    this.onRequestError(fn);
    this.onResponseError(fn);
  }

}; // Request helpers ($get, $post, ...)

var _loop = function _loop(method) {
  axiosExtra['$' + method] = function () {
    return this[method].apply(this, arguments).then(res => res && res.data);
  };
};

for (var method of ['request', 'delete', 'get', 'head', 'options', 'post', 'put', 'patch']) {
  _loop(method);
}

var extendAxiosInstance = axios => {
  for (var key in axiosExtra) {
    axios[key] = axiosExtra[key].bind(axios);
  }
};

var setupProgress = (axios, ctx) => {
  if (process.server) {
    return;
  } // A noop loading inteterface for when $nuxt is not yet ready


  var noopLoading = {
    finish: () => {},
    start: () => {},
    fail: () => {},
    set: () => {}
  };

  var $loading = () => window.$nuxt && window.$nuxt.$loading && window.$nuxt.$loading.set ? window.$nuxt.$loading : noopLoading;

  var currentRequests = 0;
  axios.onRequest(config => {
    if (config && config.progress === false) {
      return;
    }

    currentRequests++;
  });
  axios.onResponse(response => {
    if (response && response.config && response.config.progress === false) {
      return;
    }

    currentRequests--;

    if (currentRequests <= 0) {
      currentRequests = 0;
      $loading().finish();
    }
  });
  axios.onError(error => {
    if (error && error.config && error.config.progress === false) {
      return;
    }

    currentRequests--;

    if (Axios.isCancel(error)) {
      return;
    }

    $loading().fail();
    $loading().finish();
  });

  var onProgress = e => {
    if (!currentRequests) {
      return;
    }

    var progress = e.loaded * 100 / (e.total * currentRequests);
    $loading().set(Math.min(100, progress));
  };

  axios.defaults.onUploadProgress = onProgress;
  axios.defaults.onDownloadProgress = onProgress;
};

export default ((ctx, inject) => {
  // baseURL
  var baseURL = process.browser ? '/' : process.env._AXIOS_BASE_URL_ || 'http://localhost:3000'; // Create fresh objects for all default header scopes
  // Axios creates only one which is shared across SSR requests!
  // https://github.com/mzabriskie/axios/blob/master/lib/defaults.js

  var headers = {
    "common": {
      "Accept": "application/json, text/plain, */*"
    },
    "delete": {},
    "get": {},
    "head": {},
    "post": {},
    "put": {},
    "patch": {}
  };
  var axiosOptions = {
    baseURL,
    headers
  }; // Proxy SSR request headers headers

  axiosOptions.headers.common = ctx.req && ctx.req.headers ? Object.assign({}, ctx.req.headers) : {};
  delete axiosOptions.headers.common['accept'];
  delete axiosOptions.headers.common['host'];
  delete axiosOptions.headers.common['cf-ray'];
  delete axiosOptions.headers.common['cf-connecting-ip'];
  delete axiosOptions.headers.common['content-length'];
  delete axiosOptions.headers.common['content-md5'];
  delete axiosOptions.headers.common['content-type'];

  if (process.server) {
    // Don't accept brotli encoding because Node can't parse it
    axiosOptions.headers.common['accept-encoding'] = 'gzip, deflate';
  } // Create new axios instance


  var axios = Axios.create(axiosOptions);
  axios.CancelToken = Axios.CancelToken;
  axios.isCancel = Axios.isCancel; // Extend axios proto

  extendAxiosInstance(axios); // Setup interceptors

  setupProgress(axios, ctx);
  axiosRetry(axios, {}); // Inject axios to the context as $axios

  ctx.$axios = axios;
  inject('axios', axios);
});